import dynamic from 'next/dynamic';
import { convertToRaw, EditorState } from 'draft-js';
import { useCallback, useState } from 'react';
import throttle from 'lodash.throttle';
import { EditorProps } from 'react-draft-wysiwyg';

import { Wrapper } from './TextEditor.styles';
import { editorConfig } from './editorConfig';
import { ImageMapType, TextEditorPropsType } from './TextEditor.types';
import { ImageContext } from './ImageContext';

const Editor = dynamic<EditorProps>(
  () => import('react-draft-wysiwyg').then((module) => module.Editor),
  { ssr: false },
);

export function TextEditor({
  onChange,
  className,
  minHeight,
  maxHeight,
  placeholder,
  initialValue,
}: TextEditorPropsType): JSX.Element {
  const [editorState, setEditorState] = useState<EditorState>(() => {
    if (initialValue?.contentState) {
      const { contentState } = initialValue;
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  });
  const [images, setImages] = useState<ImageMapType>(() => {
    if (initialValue?.imageMap) {
      const { imageMap } = initialValue;
      return imageMap;
    }
    return null;
  });
  const handleChange = useCallback(
    throttle((newEditorState: EditorState): void => {
      const rawContent = convertToRaw(newEditorState.getCurrentContent());
      let newImages = null;
      if (images) {
        newImages = { ...images };
        Object.keys(images).forEach((src) => {
          const imageIndex = Object.values(rawContent.entityMap).findIndex(
            (entity) => entity?.data?.src === src,
          );
          if (imageIndex === -1) {
            delete newImages[src];
          }
        });
        setImages(newImages);
      }
      setEditorState(newEditorState);

      onChange({ editorState: newEditorState, imageMap: newImages });
    }),
    [images],
  );

  return (
    <Wrapper className={className} minHeight={minHeight} maxHeight={maxHeight}>
      <ImageContext.Provider value={{ images, setImages }}>
        {Editor && (
          <Editor
            editorState={editorState}
            wrapperClassName="wrapper"
            editorClassName="editor"
            toolbarClassName="toolbar"
            placeholder={placeholder}
            onEditorStateChange={handleChange}
            toolbar={editorConfig}
          />
        )}
      </ImageContext.Provider>
    </Wrapper>
  );
}

TextEditor.defaultProps = {
  className: '',
};
